
import React, { useState } from 'react';
import type { CompanyInfo } from '../types';
import { ClipboardIcon } from './icons/ClipboardIcon';
import { CheckIcon } from './icons/CheckIcon';
import { BuildingIcon } from './icons/BuildingIcon';
import { HashtagIcon } from './icons/HashtagIcon';
import { LocationIcon } from './icons/LocationIcon';
import { InvoiceIcon } from './icons/InvoiceIcon';
import { InvoiceModal } from './InvoiceModal';

interface CompanyCardProps {
  info: CompanyInfo;
}

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value?: string }> = ({ icon, label, value }) => (
  <div className="flex items-start text-sm mb-3">
    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-slate-500 dark:text-slate-400">{icon}</div>
    <div className="ml-3 flex-1">
      <p className="font-semibold text-slate-600 dark:text-slate-300">{label}</p>
      <p className="text-slate-800 dark:text-slate-200 break-words">{value || 'N/A'}</p>
    </div>
  </div>
);


export const CompanyCard: React.FC<CompanyCardProps> = ({ info }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);

  const handleCopy = () => {
    const textToCopy = `
公司全稱: ${info.fullName}
統一編號: ${info.unifiedBusinessNumber}
註冊地址: ${info.address}
`.trim();
    navigator.clipboard.writeText(textToCopy);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 transition-transform hover:scale-[1.02] duration-300">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-primary-700 dark:text-primary-400 pr-4 flex items-center">
            <BuildingIcon className="w-6 h-6 mr-2 flex-shrink-0" />
            <span>{info.fullName}</span>
          </h3>
          {/* 按鈕群組：垂直排列 */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            {/* 複製按鈕 */}
            <button
              onClick={handleCopy}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-200 ${isCopied
                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              aria-label={isCopied ? '已複製' : '複製資訊'}
              title={isCopied ? '已複製' : '複製資訊'}
            >
              {isCopied ? <CheckIcon className="w-5 h-5" /> : <ClipboardIcon className="w-5 h-5" />}
            </button>

            {/* 發票按鈕 */}
            <button
              onClick={() => setIsInvoiceOpen(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-800/60 hover:scale-110 hover:shadow-md"
              aria-label="開立統一發票"
              title="開立統一發票"
            >
              <InvoiceIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <InfoRow icon={<HashtagIcon className="w-5 h-5" />} label="統一編號" value={info.unifiedBusinessNumber} />
          <InfoRow icon={<LocationIcon className="w-5 h-5" />} label="註冊地址" value={info.address} />
        </div>

        {/* 發票按鈕下方標籤提示 */}
        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700 flex justify-end">
          <button
            onClick={() => setIsInvoiceOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 border border-amber-200 dark:border-amber-700/50 transition-all duration-200 hover:shadow-sm"
          >
            <InvoiceIcon className="w-3.5 h-3.5" />
            開立統一發票
          </button>
        </div>
      </div>

      {/* 發票 Modal */}
      <InvoiceModal
        isOpen={isInvoiceOpen}
        onClose={() => setIsInvoiceOpen(false)}
        companyName={info.fullName}
        companyTaxId={info.unifiedBusinessNumber}
      />
    </>
  );
};
